import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';

/**
 * 3D-tilted browser/app mockup with an optional 4-image flip carousel.
 *
 * Two modes:
 *   1. Children mode — pass any children (image, JSX) inside the browser
 *      chrome. The whole card tilts subtly with the pointer.
 *   2. Screens mode — pass `screens` (array of image URLs). The card
 *      flips between screens on rotateY. Which screen is shown is either
 *      controlled by `activeIndex` (if provided) or auto-cycles every
 *      `intervalMs` (default 4000ms).
 *
 * Props:
 *   url             — string shown in the address bar
 *   className       — extra classes on the outer wrapper
 *   baseRotateX     — resting X rotation in degrees (default 8)
 *   baseRotateY     — resting Y rotation in degrees (default -6)
 *   tiltStrength    — pointer-follow tilt distance (default 6°)
 *   screens         — [{ src, alt }] array; enables carousel mode
 *   activeIndex     — 0-based active screen (optional)
 *   intervalMs      — auto-cycle period when activeIndex not provided
 *   captions        — array of caption strings (optional, one per screen)
 */
const TiltedAppPreview = ({
  url = 'app.esgcaffe.ai/dashboard',
  className = '',
  baseRotateX = 8,
  baseRotateY = -6,
  tiltStrength = 6,
  children,
  screens,
  activeIndex,
  intervalMs = 4000,
  captions,
}) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 20 });
  const sy = useSpring(my, { stiffness: 120, damping: 20 });
  const rotateX = useTransform(sy, [-1, 1], [baseRotateX + tiltStrength, baseRotateX - tiltStrength]);
  const rotateY = useTransform(sx, [-1, 1], [baseRotateY - tiltStrength, baseRotateY + tiltStrength]);

  // Auto-cycle when in screens mode and no controlled index
  const [autoIdx, setAutoIdx] = useState(0);
  const inScreensMode = Array.isArray(screens) && screens.length > 0;
  const controlled = typeof activeIndex === 'number';
  const currentIdx = inScreensMode
    ? ((controlled ? activeIndex : autoIdx) % screens.length + screens.length) % screens.length
    : 0;

  useEffect(() => {
    if (!inScreensMode || controlled) return;
    const id = window.setInterval(() => setAutoIdx((i) => i + 1), intervalMs);
    return () => window.clearInterval(id);
  }, [inScreensMode, controlled, intervalMs]);

  const onMove = (e) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    mx.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
    my.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const activeCaption = captions && inScreensMode ? captions[currentIdx] : null;

  return (
    <div
      className={`relative ${className}`}
      style={{ perspective: '1600px' }}
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {/* Glow behind */}
      <div
        className="pointer-events-none absolute -inset-10 rounded-[2rem] blur-3xl"
        style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.14) 0%, rgba(16,185,129,0.10) 100%)' }}
      />
      <motion.div
        className="relative rounded-2xl overflow-hidden border border-slate-200/80 bg-white will-change-transform"
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
          boxShadow:
            '0 50px 120px -30px rgba(15,23,42,0.35), 0 20px 40px -15px rgba(15,23,42,0.15)',
        }}
      >
        {/* Chrome */}
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]" />
          <span className="ml-4 flex-1 text-center text-[11px] font-medium tracking-wide text-slate-400">
            {url}
          </span>
        </div>

        {inScreensMode ? (
          <div className="relative bg-white" style={{ perspective: '1600px' }}>
            {/* Sizer: the currently-active image renders in flow (invisible)
                so the container's height matches its natural aspect ratio.
                The visible, animated copy is layered absolutely on top. */}
            <img
              src={screens[currentIdx].src}
              alt=""
              aria-hidden="true"
              className="block w-full h-auto opacity-0 pointer-events-none select-none"
              draggable="false"
            />

            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={currentIdx}
                src={screens[currentIdx].src}
                alt={screens[currentIdx].alt || `Screen ${currentIdx + 1}`}
                className="absolute inset-0 h-full w-full object-contain"
                initial={{ opacity: 0, rotateY: 90, scale: 0.96 }}
                animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                exit={{ opacity: 0, rotateY: -90, scale: 0.96 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformStyle: 'preserve-3d', backfaceVisibility: 'hidden' }}
                loading="eager"
                draggable="false"
              />
            </AnimatePresence>

            {/* Caption + progress dots — pill overlay below content edge */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 rounded-full bg-slate-900/70 backdrop-blur px-4 py-1.5">
              {activeCaption && (
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/95">
                  {activeCaption}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                {screens.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      i === currentIdx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          children
        )}
      </motion.div>
    </div>
  );
};

export default TiltedAppPreview;
